import { DISCLAIMER } from "../dashboard/Precharting/utils/chapidata";

const CONSENT_DISCLAIMER_PREFIX = "Patient or surrogate consented to the use of the AI Scribe";

export type CarryForwardSource = {
  key: string;
  label: string;
  text: string;
  sectionNames: string[];
  fallbackSectionNames: string[];
};

export type CarryForwardRange = {
  start: number;
  end: number;
};

export type NoteSection = {
  start: number;
  end: number;
  name: string;
};

type ChartHistoryRecord = Record<string, unknown>;

type ChapiField = {
  key: string;
  label: string;
  aliases: string[];
  sectionNames: string[];
  fallbackSectionNames: string[];
};

const HPI_FALLBACK_SECTION_NAMES = ["hpi", "history of present illness"];
const OBJECTIVE_FALLBACK_SECTION_NAMES = ["objective", "physical exam"];

type HeadingBlock = {
  heading: string;
  body: string;
};

type Sentence = {
  text: string;
  start: number;
  end: number;
};

export const CARRY_FORWARD_CHAPI_FIELDS: ChapiField[] = [
  {
    key: "allergies",
    label: "Allergies",
    aliases: ["allergies"],
    sectionNames: ["allergies"],
    fallbackSectionNames: HPI_FALLBACK_SECTION_NAMES,
  },
  {
    key: "vitals",
    label: "Vitals",
    aliases: ["vitals"],
    sectionNames: ["vitals", "vital signs"],
    fallbackSectionNames: OBJECTIVE_FALLBACK_SECTION_NAMES,
  },
  {
    key: "past_medical_history",
    label: "Active Problems",
    aliases: ["past_medical_history", "problems"],
    sectionNames: ["active problems", "past medical history", "medical history"],
    fallbackSectionNames: HPI_FALLBACK_SECTION_NAMES,
  },
  {
    key: "medications",
    label: "Medications",
    aliases: ["medications"],
    sectionNames: ["medications", "current psychiatric medications"],
    fallbackSectionNames: HPI_FALLBACK_SECTION_NAMES,
  },
  {
    key: "labs",
    label: "Labs",
    aliases: ["lab_results", "labs"],
    sectionNames: ["labs", "lab results", "laboratory data"],
    fallbackSectionNames: OBJECTIVE_FALLBACK_SECTION_NAMES,
  },
];

const SECTION_NAME_ALIASES: Record<string, string[]> = {
  assessment: ["impression", "impressionplan"],
  impression: ["assessment"],
  impressionplan: ["assessment", "assessmentplan", "assessmentandplan"],
  assessmentplan: ["assessment", "plan", "assessmentandplan"],
  assessmentandplan: ["assessment", "plan", "assessmentplan"],
  assessmentplanandfollowup: ["assessmentandplan", "assessmentplan", "assessment", "plan"],
  assessmentandplanandfollowup: ["assessmentandplan", "assessmentplan", "assessment", "plan"],
  hpi: ["historyofpresentillness"],
  historyofpresentillness: ["hpi"],
  labresults: ["labs", "laboratorydata"],
  labs: ["labresults", "laboratorydata"],
  laboratorydata: ["labs", "labresults"],
  vitals: ["vitalsigns"],
  vitalsigns: ["vitals"],
  medications: ["currentpsychiatricmedications"],
  currentpsychiatricmedications: ["medications"],
  activeproblems: ["pastmedicalhistory", "medicalhistory"],
  pastmedicalhistory: ["activeproblems", "medicalhistory"],
  medicalhistory: ["activeproblems", "pastmedicalhistory"],
};

const KNOWN_NOTE_SECTION_NAMES = new Set(
  [
    "allergies",
    "vitals",
    "vital signs",
    "medications",
    "current psychiatric medications",
    "labs",
    "lab results",
    "laboratory data",
    "active problems",
    "past medical history",
    "medical history",
    "hpi",
    "history of present illness",
    "interval history",
    "plan",
    "assessment",
    "assessment and plan",
    "assessment plan",
    "assessment/plan & follow-up",
    "assessment and plan and follow-up",
    "follow-up",
    "impression",
    "impression plan",
    "subjective",
    "objective",
    "physical exam",
    "review of systems",
    "ros",
  ].map((name) => normalizeSectionName(name))
);

const DISCUSSION_WORDS = ["asked", "recommend", "consider", "discussed", "suggested", "propose"];
const CLAUSE_BREAK_WORDS = new Set(["and", "but", "or"]);
const NEGATION_WORDS = new Set([
  "no",
  "not",
  "never",
  "denies",
  "denied",
  "deny",
  "without",
  "negative",
  "none",
  "unlikely",
]);
const CONTRACTION_NEGATIONS = new Set(["doesnt", "didnt", "isnt", "wasnt", "cannot", "cant"]);
const PLAN_OR_ASSESSMENT_NAMES = new Set(
  [
    "plan",
    "assessment",
    "assessment and plan",
    "assessment plan",
    "assessment/plan & follow-up",
    "assessment and plan and follow-up",
    "follow-up",
    "impression",
    "impression plan",
  ].map((name) => normalizeSectionName(name))
);

export const CARRY_FORWARD_MATCH_THRESHOLD = 0.85;

const HEADING_WITH_COLON =
  /^[ \t]*#{0,6}[ \t]*([A-Za-z][A-Za-z0-9 &/'()-]{0,80}?)[ \t]*:[ \t]*(.*)$/;
const HEADING_WITHOUT_COLON = /^[ \t]*#{0,6}[ \t]*([A-Z][A-Z0-9 &/'()-]{1,80})[ \t]*$/;

export function normalizeSectionName(value = ""): string {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\+/g, " and ")
    .replace(/[^a-z0-9]+/g, "");
}

function getSectionNameCandidates(name: string): string[] {
  const normalized = normalizeSectionName(name);
  const aliases = SECTION_NAME_ALIASES[normalized] || [];
  return [normalized, ...aliases.map((alias) => normalizeSectionName(alias))].filter(Boolean);
}

function expandSectionNames(names: string[] = []): string[] {
  return [...new Set(names.flatMap((name) => getSectionNameCandidates(name)))];
}

function getFallbackSectionNamesForHeading(heading: string): string[] {
  const headingCandidates = new Set(getSectionNameCandidates(heading));
  const field = CARRY_FORWARD_CHAPI_FIELDS.find((candidate) =>
    expandSectionNames(candidate.sectionNames).some((name) => headingCandidates.has(name))
  );

  return field ? expandSectionNames(field.fallbackSectionNames) : [];
}

function isKnownSectionName(name: string): boolean {
  return getSectionNameCandidates(name).some((candidate) =>
    KNOWN_NOTE_SECTION_NAMES.has(candidate)
  );
}

function parseChartHistory(rawChartHistory: unknown): ChartHistoryRecord {
  if (!rawChartHistory) return {};
  if (typeof rawChartHistory === "string") {
    try {
      const parsed = JSON.parse(rawChartHistory);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof rawChartHistory === "object" && !Array.isArray(rawChartHistory)) {
    return rawChartHistory as ChartHistoryRecord;
  }
  return {};
}

function toPlainText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.replace(/<[^>]+>/g, " ");
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function hasMeaningfulSourceText(value: unknown): boolean {
  return toPlainText(value).trim().length > 0;
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
}

export function getAdditionalEhrContextText(chartHistory: unknown): string {
  const parsed = parseChartHistory(chartHistory);
  const customTemplate = toPlainText(parsed.custom_template).trim();
  if (customTemplate) return customTemplate;
  return toPlainText(parsed.additional_context).trim();
}

export function isAdditionalEhrContextSelected(chartHistory: unknown): boolean {
  const parsed = parseChartHistory(chartHistory);
  return isTruthyFlag(parsed.is_custom_template) || hasMeaningfulSourceText(parsed.custom_template);
}

export function parseAdditionalEhrContextSources(text: string): CarryForwardSource[] {
  return splitHeadingBlocks(text)
    .filter((block) => isKnownSectionName(block.heading) && hasMeaningfulSourceText(block.body))
    .map((block) => ({
      key: normalizeSectionName(block.heading),
      label: block.heading.trim(),
      text: block.body.trim(),
      sectionNames: getSectionNameCandidates(block.heading),
      fallbackSectionNames: getFallbackSectionNamesForHeading(block.heading),
    }));
}

export function getSelectedChartHistorySources(chartHistory: unknown): CarryForwardSource[] {
  const parsed = parseChartHistory(chartHistory);

  return CARRY_FORWARD_CHAPI_FIELDS.flatMap((field) => {
    const value = field.aliases
      .map((alias) => parsed[alias])
      .find((candidate) => hasMeaningfulSourceText(candidate));

    if (!hasMeaningfulSourceText(value)) return [];

    return [
      {
        key: field.key,
        label: field.label,
        text: toPlainText(value).trim(),
        sectionNames: expandSectionNames(field.sectionNames),
        fallbackSectionNames: expandSectionNames(field.fallbackSectionNames),
      },
    ];
  });
}

export function getCarryForwardSources(chartHistory: unknown): CarryForwardSource[] {
  const additionalEhrSources = isAdditionalEhrContextSelected(chartHistory)
    ? parseAdditionalEhrContextSources(getAdditionalEhrContextText(chartHistory))
    : [];

  return [...additionalEhrSources, ...getSelectedChartHistorySources(chartHistory)];
}

export function hasCarryForwardHighlightSources(chartHistory: unknown): boolean {
  return getCarryForwardSources(chartHistory).length > 0;
}

function splitHeadingBlocks(text: string): HeadingBlock[] {
  const blocks: HeadingBlock[] = [];
  let heading = "";
  let bodyLines: string[] = [];

  const flush = () => {
    const body = bodyLines.join("\n").trim();
    if (heading || body) {
      blocks.push({ heading, body });
    }
    heading = "";
    bodyLines = [];
  };

  String(text || "")
    .split("\n")
    .forEach((line) => {
      const parsedHeading = parseHeadingLine(line);
      if (parsedHeading && isKnownSectionName(parsedHeading.title)) {
        flush();
        heading = parsedHeading.title;
        if (parsedHeading.sameLineBody) {
          bodyLines.push(parsedHeading.sameLineBody);
        }
        return;
      }

      bodyLines.push(line);
    });

  flush();
  return blocks;
}

export function normalizeCarryForwardText(value = ""): string {
  let text = "";
  let lastWasSpace = true;

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];

    if (isHyphenOrDash(char)) {
      continue;
    }

    if (/[a-z0-9]/i.test(char)) {
      if (lastWasSpace && text.length > 0 && shouldJoinNumberAndUnit(text, char)) {
        text = text.slice(0, -1);
      }
      text += char.toLowerCase();
      lastWasSpace = false;
      continue;
    }

    if (!lastWasSpace) {
      text += " ";
      lastWasSpace = true;
    }
  }

  if (text.endsWith(" ")) {
    text = text.slice(0, -1);
  }

  return text;
}

function isHyphenOrDash(char: string): boolean {
  return char === "-" || char === "–" || char === "—" || char === "−";
}

function shouldJoinNumberAndUnit(normalizedSoFar: string, nextChar: string): boolean {
  const previous = normalizedSoFar[normalizedSoFar.length - 2];
  return Boolean(previous && /\d/.test(previous) && /[a-z%]/i.test(nextChar));
}

function parseHeadingLine(line: string): { title: string; sameLineBody: string } | null {
  const withColon = line.match(HEADING_WITH_COLON);
  if (withColon) {
    return { title: withColon[1].trim(), sameLineBody: withColon[2].trim() };
  }

  const withoutColon = line.match(HEADING_WITHOUT_COLON);
  if (withoutColon) {
    return { title: withoutColon[1].trim(), sameLineBody: "" };
  }

  return null;
}

export function buildNoteSectionIndex(noteText = ""): NoteSection[] {
  const sections: NoteSection[] = [];
  let lineStart = 0;
  let current: NoteSection | null = null;

  String(noteText)
    .split("\n")
    .forEach((line) => {
      if (isConsentDisclaimerLine(line) && current) {
        current.end = lineStart;
        current = null;
        lineStart += line.length + 1;
        return;
      }

      const heading = parseHeadingLine(line);
      if (heading && isKnownSectionName(heading.title)) {
        if (current) {
          current.end = lineStart;
        }
        current = {
          name: normalizeSectionName(heading.title),
          start: lineStart,
          end: noteText.length,
        };
        sections.push(current);
      }

      lineStart += line.length + 1;
    });

  return sections;
}

function findSectionByNames(sectionIndex: NoteSection[], names: string[]): NoteSection | null {
  const candidates = new Set(expandSectionNames(names));

  return (
    sectionIndex.find((section) => {
      const sectionNames = getSectionNameCandidates(section.name);
      return sectionNames.some((name) => candidates.has(name));
    }) || null
  );
}

export function findMatchingNoteSection(
  sectionIndex: NoteSection[],
  source: CarryForwardSource
): NoteSection | null {
  return resolveMatchingNoteSection(sectionIndex, source)?.noteSection || null;
}

function resolveMatchingNoteSection(
  sectionIndex: NoteSection[],
  source: CarryForwardSource
): { noteSection: NoteSection; isFallback: boolean } | null {
  const dedicated = findSectionByNames(sectionIndex, source.sectionNames);
  if (dedicated) return { noteSection: dedicated, isFallback: false };

  const fallback = findSectionByNames(sectionIndex, source.fallbackSectionNames);
  if (fallback) return { noteSection: fallback, isFallback: true };

  return null;
}

export function splitIntoSentences(sectionText = ""): Sentence[] {
  const sentences: Sentence[] = [];
  const pattern = /[^.!?]+(?:[.!?]+|$)/g;
  let match = pattern.exec(sectionText);

  while (match) {
    const raw = match[0];
    const leading = raw.match(/^\s*/)?.[0].length || 0;
    const trailing = raw.match(/\s*$/)?.[0].length || 0;
    const start = match.index + leading;
    const end = match.index + raw.length - trailing;
    const text = sectionText.slice(start, end);

    if (text) {
      sentences.push({ text, start, end });
    }

    match = pattern.exec(sectionText);
  }

  return sentences;
}

function splitSentenceIntoListItems(sentence: Sentence): Sentence[] {
  const items: Sentence[] = [];
  let itemStart = 0;

  for (let index = 0; index <= sentence.text.length; index += 1) {
    if (index < sentence.text.length && sentence.text[index] !== ",") continue;

    const raw = sentence.text.slice(itemStart, index);
    const leading = raw.match(/^\s*/)?.[0].length || 0;
    const trailing = raw.match(/\s*$/)?.[0].length || 0;
    const start = sentence.start + itemStart + leading;
    const end = sentence.start + index - trailing;
    const text = sentence.text.slice(itemStart + leading, index - trailing);

    if (text) {
      items.push({ text, start, end });
    }

    itemStart = index + 1;
  }

  return items.length ? items : [sentence];
}

function splitIntoHighlightUnits(sectionText = ""): Sentence[] {
  const units: Sentence[] = [];
  let lineStart = 0;

  sectionText.split("\n").forEach((line) => {
    splitIntoSentences(line).forEach((sentence) => {
      splitSentenceIntoListItems({
        text: sentence.text,
        start: lineStart + sentence.start,
        end: lineStart + sentence.end,
      }).forEach((item) => {
        units.push(item);
      });
    });
    lineStart += line.length + 1;
  });

  return units;
}

export function isConfirmedStatement(sentence: string, sectionName = ""): boolean {
  const trimmed = sentence.trim();
  if (!trimmed) return false;
  if (trimmed.endsWith("?")) return false;

  const isPlanOrAssessment = getSectionNameCandidates(sectionName).some((name) =>
    PLAN_OR_ASSESSMENT_NAMES.has(name)
  );
  if (isPlanOrAssessment) return true;

  const haystack = ` ${normalizeCarryForwardText(trimmed)} `;
  return !DISCUSSION_WORDS.some((word) => haystack.includes(` ${word} `));
}

function getNormalizedTokens(value: string): string[] {
  const withoutApostrophes = String(value || "").replace(/['’]/g, "");
  return normalizeCarryForwardText(withoutApostrophes).split(" ").filter(Boolean);
}

function tokensForNegation(value: string): string[] {
  return getNormalizedTokens(value).map((token) =>
    CONTRACTION_NEGATIONS.has(token) ? "not" : token
  );
}

function findContiguousTokenRun(haystack: string[], needle: string[]): number {
  if (!needle.length || haystack.length < needle.length) return -1;

  for (let index = 0; index <= haystack.length - needle.length; index += 1) {
    if (needle.every((token, offset) => haystack[index + offset] === token)) {
      return index;
    }
  }

  return -1;
}

function clauseAroundMatch(tokens: string[], matchStart: number, matchLength: number): string[] {
  let start = matchStart;
  while (start > 0 && !CLAUSE_BREAK_WORDS.has(tokens[start - 1])) {
    start -= 1;
  }

  let end = matchStart + matchLength;
  while (end < tokens.length && !CLAUSE_BREAK_WORDS.has(tokens[end])) {
    end += 1;
  }

  return tokens.slice(start, end);
}

function hasNegationCue(tokens: string[]): boolean {
  return tokens.some((token) => NEGATION_WORDS.has(token));
}

export function isNegatedSourceMatch(savedText: string, sentenceText: string): boolean {
  const savedTokens = tokensForNegation(savedText);
  const sentenceTokens = tokensForNegation(sentenceText);
  if (!savedTokens.length || !sentenceTokens.length) return false;
  if (hasNegationCue(savedTokens)) return false;

  const matchStart = findContiguousTokenRun(sentenceTokens, savedTokens);
  if (matchStart === -1) return false;

  return hasNegationCue(clauseAroundMatch(sentenceTokens, matchStart, savedTokens.length));
}

function levenshteinRatio(left: string, right: string): number {
  if (left === right) return 1;
  if (!left || !right) return 0;

  const rows = left.length + 1;
  const cols = right.length + 1;
  const grid = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let row = 0; row < rows; row += 1) grid[row][0] = row;
  for (let col = 0; col < cols; col += 1) grid[0][col] = col;

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      grid[row][col] = Math.min(
        grid[row - 1][col] + 1,
        grid[row][col - 1] + 1,
        grid[row - 1][col - 1] + cost
      );
    }
  }

  return 1 - grid[left.length][right.length] / Math.max(left.length, right.length);
}

function tokenOverlap(leftTokens: string[], rightTokens: string[]): number {
  if (!leftTokens.length || !rightTokens.length) return 0;

  const right = new Set(rightTokens);
  const overlap = leftTokens.filter((token) => right.has(token)).length;
  const union = new Set([...leftTokens, ...rightTokens]).size;
  return union === 0 ? 0 : overlap / union;
}

export function scoreCarryForwardMatch(savedText: string, sentenceText: string): number {
  const savedTokens = getNormalizedTokens(savedText);
  const sentenceTokens = getNormalizedTokens(sentenceText);
  if (!savedTokens.length || !sentenceTokens.length) return 0;
  if (findContiguousTokenRun(sentenceTokens, savedTokens) !== -1) return 1;

  const overlap = tokenOverlap(savedTokens, sentenceTokens);
  if (overlap >= CARRY_FORWARD_MATCH_THRESHOLD) return overlap;

  if (savedTokens.length <= 2) {
    const tokenScores = savedTokens.map((token) => {
      if (token.length < 4) {
        return sentenceTokens.includes(token) ? 1 : 0;
      }

      return sentenceTokens.reduce(
        (best, sentenceToken) => Math.max(best, levenshteinRatio(token, sentenceToken)),
        0
      );
    });
    if (tokenScores.length) return Math.min(...tokenScores);
  }

  return overlap;
}

function getSourceItems(text: string): string[] {
  const items = new Set<string>();
  const trimmed = text.trim();
  if (trimmed) items.add(trimmed);

  trimmed.split(/\n+/).forEach((line) => {
    const cleaned = line.replace(/^\s*(?:\(?\d+[.)]|[-*•])\s+/, "").trim();
    if (cleaned) items.add(cleaned);

    cleaned.split(",").forEach((part) => {
      const item = part.trim();
      if (item) items.add(item);
    });
  });

  splitIntoSentences(trimmed).forEach((sentence) => {
    if (sentence.text.trim()) items.add(sentence.text.trim());
  });

  return [...items];
}

function isInsertedSourceLine(savedText: string, sentenceText: string): boolean {
  return scoreCarryForwardMatch(sentenceText, savedText) >= CARRY_FORWARD_MATCH_THRESHOLD;
}

export function findSourceMatchesInSection({
  noteText,
  noteSection,
  source,
  requireInsertedLine = false,
}: {
  noteText: string;
  noteSection: NoteSection;
  source: CarryForwardSource;
  requireInsertedLine?: boolean;
}): CarryForwardRange[] {
  const sectionText = noteText.slice(noteSection.start, noteSection.end);
  const items = getSourceItems(source.text);
  const ranges: CarryForwardRange[] = [];

  splitIntoHighlightUnits(sectionText).forEach((sentence) => {
    if (!isConfirmedStatement(sentence.text, noteSection.name)) return;

    const matches = items.some((item) => {
      if (scoreCarryForwardMatch(item, sentence.text) < CARRY_FORWARD_MATCH_THRESHOLD) return false;
      if (isNegatedSourceMatch(item, sentence.text)) return false;
      return !requireInsertedLine || isInsertedSourceLine(item, sentence.text);
    });
    if (!matches) return;

    ranges.push({
      start: noteSection.start + sentence.start,
      end: noteSection.start + sentence.end,
    });
  });

  return ranges;
}

function isConsentDisclaimerLine(line = ""): boolean {
  return line.trim().startsWith(CONSENT_DISCLAIMER_PREFIX);
}

function getConsentDisclaimerRanges(noteText = ""): CarryForwardRange[] {
  const snippets = [DISCLAIMER, `${CONSENT_DISCLAIMER_PREFIX}.`];
  const ranges: CarryForwardRange[] = [];

  snippets.forEach((snippet) => {
    let from = 0;
    while (from < noteText.length) {
      const index = noteText.indexOf(snippet, from);
      if (index === -1) break;
      ranges.push({ start: index, end: index + snippet.length });
      from = index + snippet.length;
    }
  });

  return mergeCarryForwardRanges(ranges);
}

export function subtractRanges(
  ranges: CarryForwardRange[],
  excluded: CarryForwardRange[]
): CarryForwardRange[] {
  const remaining = excluded.reduce((current, block) => {
    return current.flatMap((range) => {
      if (range.end <= block.start || range.start >= block.end) return [range];

      const pieces: CarryForwardRange[] = [];
      if (range.start < block.start) {
        pieces.push({ start: range.start, end: block.start });
      }
      if (range.end > block.end) {
        pieces.push({ start: block.end, end: range.end });
      }
      return pieces;
    });
  }, ranges);

  return mergeCarryForwardRanges(remaining);
}

export function mergeCarryForwardRanges(ranges: CarryForwardRange[] = []): CarryForwardRange[] {
  return ranges
    .filter(
      (range) =>
        Number.isFinite(range.start) && Number.isFinite(range.end) && range.end > range.start
    )
    .sort((left, right) => left.start - right.start || left.end - right.end)
    .reduce<CarryForwardRange[]>((merged, range) => {
      const previous = merged[merged.length - 1];
      if (!previous || range.start > previous.end) {
        merged.push({ ...range });
        return merged;
      }

      previous.end = Math.max(previous.end, range.end);
      return merged;
    }, []);
}

export type NoteHighlightKind = "vvcPlaceholder" | "carryForward";

export type NoteHighlightRange = CarryForwardRange & { kind: NoteHighlightKind };

export function buildNoteHighlightRanges(
  vvcPlaceholderRanges: CarryForwardRange[] = [],
  carryForwardHighlightRanges: CarryForwardRange[] = []
): NoteHighlightRange[] {
  const vvcRanges: NoteHighlightRange[] = mergeCarryForwardRanges(vvcPlaceholderRanges).map(
    (range) => ({ ...range, kind: "vvcPlaceholder" })
  );
  const carryForwardRanges: NoteHighlightRange[] = subtractRanges(
    carryForwardHighlightRanges,
    vvcRanges
  ).map((range) => ({ ...range, kind: "carryForward" }));

  return [...vvcRanges, ...carryForwardRanges].sort((left, right) => left.start - right.start);
}

export function buildCarryForwardHighlightRanges(
  noteText = "",
  chartHistory: unknown = {}
): CarryForwardRange[] {
  if (!noteText) return [];

  const sources = getCarryForwardSources(chartHistory);
  if (!sources.length) return [];

  const sectionIndex = buildNoteSectionIndex(noteText);
  const ranges = sources.flatMap((source) => {
    const match = resolveMatchingNoteSection(sectionIndex, source);
    if (!match) return [];
    return findSourceMatchesInSection({
      noteText,
      noteSection: match.noteSection,
      source,
      requireInsertedLine: match.isFallback,
    });
  });

  return subtractRanges(mergeCarryForwardRanges(ranges), getConsentDisclaimerRanges(noteText));
}
